import React from "react";
import { Filter, Info, ChevronUp, ChevronDown, X, Search } from "lucide-react";
import HelpButton from "./HelpButton";

const FilterPanel = (props) => {
  const {
    filters,
    searchQuery,
    setSearchQuery,
    handleFilterChange,
    showFilters,
    setShowFilters,
    handleMouseEnter,
    handleMouseLeave,
    onApplyFilters,
  } = props;
  return (
    <div className="insights-header">
      <div className="search-and-filter">
        <div className="insights-title-container">
          <h2>Value Insights</h2>
          <HelpButton />
        </div>

        <div className="insights-controls">
          <div className="enhanced-search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search by stock name or symbol..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="clear-search"
                onClick={() => setSearchQuery("")}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <button
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={20} />
            <span>Filters</span>
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="enhanced-filter-panel">
          <div className="filter-group">
            <label>Discount to Intrinsic Value</label>
            <div className="enhanced-range-filter">
              <span className="range-value">{filters.discountRange[0]}%</span>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="90"
                  value={filters.discountRange[0]}
                  onChange={(e) =>
                    handleFilterChange("discountRange", [
                      parseInt(e.target.value),
                      filters.discountRange[1],
                    ])
                  }
                  className="enhanced-slider"
                />
                <div className="slider-track"></div>
              </div>
              <span className="range-connector">to</span>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="90"
                  value={filters.discountRange[1]}
                  onChange={(e) =>
                    handleFilterChange("discountRange", [
                      filters.discountRange[0],
                      parseInt(e.target.value),
                    ])
                  }
                  className="enhanced-slider"
                />
                <div className="slider-track"></div>
              </div>
              <span className="range-value">{filters.discountRange[1]}%</span>
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label>Price Range</label>
              <div className="enhanced-range-filter">
                <span className="range-value">
                  ${filters.priceRange ? filters.priceRange[0] : 0}
                </span>
                <div className="slider-container">
                  <input
                    type="range"
                    min="0"
                    max="1000"
                    value={filters.priceRange ? filters.priceRange[0] : 0}
                    onChange={(e) =>
                      handleFilterChange("priceRange", [
                        parseInt(e.target.value),
                        filters.priceRange ? filters.priceRange[1] : 1000,
                      ])
                    }
                    className="enhanced-slider"
                  />
                  <div className="slider-track"></div>
                </div>
                <span className="range-connector">to</span>
                <div className="slider-container">
                  <input
                    type="range"
                    min="0"
                    max="1000"
                    value={filters.priceRange ? filters.priceRange[1] : 1000}
                    onChange={(e) =>
                      handleFilterChange("priceRange", [
                        filters.priceRange ? filters.priceRange[0] : 0,
                        parseInt(e.target.value),
                      ])
                    }
                    className="enhanced-slider"
                  />
                  <div className="slider-track"></div>
                </div>
                <span className="range-value">
                  ${filters.priceRange ? filters.priceRange[1] : 1000}
                </span>
              </div>
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label>Market Cap</label>
              <select
                value={filters.marketCap}
                onChange={(e) =>
                  handleFilterChange("marketCap", e.target.value)
                }
                className="enhanced-select"
              >
                <option value="all">All Market Caps</option>
                <option value="large">Large Cap (&gt;$10B)</option>
                <option value="mid">Mid Cap ($2B-$10B)</option>
                <option value="small">Small Cap (&lt;$2B)</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sector</label>
              <select
                value={filters.sector}
                onChange={(e) => handleFilterChange("sector", e.target.value)}
                className="enhanced-select"
              >
                <option value="all">All Sectors</option>
                <option value="Technology">Technology</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Financial">Financial</option>
                <option value="Consumer">Consumer</option>
                <option value="Energy">Energy</option>
                <option value="Industrial">Industrial</option>
              </select>
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label>
                Minimum ROIC %
                <span
                  className="info-icon"
                  onMouseEnter={(e) =>
                    handleMouseEnter(
                      "Return on Invested Capital - Higher values indicate more efficient use of capital",
                      e
                    )
                  }
                  onMouseLeave={handleMouseLeave}
                >
                  <Info size={16} />
                </span>
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={filters.minROIC}
                onChange={(e) =>
                  handleFilterChange("minROIC", parseInt(e.target.value) || 0)
                }
                className="enhanced-input"
              />
            </div>

            <div className="filter-group">
              <label>
                Minimum Operating Margin %
                <span
                  className="info-icon"
                  onMouseEnter={(e) =>
                    handleMouseEnter(
                      "Operating profit margin - Higher values indicate better profitability",
                      e
                    )
                  }
                  onMouseLeave={handleMouseLeave}
                >
                  <Info size={16} />
                </span>
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={filters.minMargin}
                onChange={(e) =>
                  handleFilterChange("minMargin", parseInt(e.target.value) || 0)
                }
                className="enhanced-input"
              />
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label>Sort By</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                className="enhanced-select"
              >
                <option value="discount">Discount to Value</option>
                <option value="roic">Return on Invested Capital</option>
                <option value="margin">Operating Margin</option>
                <option value="growth">Growth Rate</option>
                <option value="price">Price (Low to High)</option>
                <option value="priceDesc">Price (High to Low)</option>
              </select>
            </div>

            <div className="filter-group">
              <label>
                Number of Stocks to Parse
                <span
                  className="info-icon"
                  onMouseEnter={(e) =>
                    handleMouseEnter(
                      "Higher numbers use more API calls. Recommend starting with fewer stocks.",
                      e
                    )
                  }
                  onMouseLeave={handleMouseLeave}
                >
                  <Info size={16} />
                </span>
              </label>
              <select
                value={filters.stocksLimit || 10}
                onChange={(e) =>
                  handleFilterChange("stocksLimit", parseInt(e.target.value))
                }
                className="enhanced-select"
              >
                <option value="5">5 stocks</option>
                <option value="10">10 stocks</option>
                <option value="15">15 stocks</option>
                <option value="20">20 stocks</option>
                <option value="30">30 stocks</option>
                <option value="40">40 stocks (maximum)</option>
              </select>
            </div>
          </div>

          <div className="filter-actions">
            <button
              className="reset-filters-button"
              onClick={() => {
                handleFilterChange("discountRange", [10, 40]);
                handleFilterChange("priceRange", [0, 1000]);
                handleFilterChange("marketCap", "all");
                handleFilterChange("sector", "all");
                handleFilterChange("minROIC", 0);
                handleFilterChange("minMargin", 0);
                handleFilterChange("sortBy", "discount");
                handleFilterChange("stocksLimit", 10);
              }}
            >
              Reset Filters
            </button>
            <button
              className="apply-filters-button"
              onClick={props.onApplyFilters}
            >
              Apply Filters & Search
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
